/**
 * Security Utilities
 *
 * This module provides security utilities for input sanitization,
 * XSS prevention, and safe DOM manipulation.
 */

import DOMPurify from 'dompurify';
import logger from './logger';
import { detectInjections, sanitizeInput, InjectionDetectionResult } from './injection-detection';

/**
 * Sanitize HTML content to prevent XSS attacks
 *
 * @param dirty - Potentially unsafe HTML string
 * @param options - DOMPurify configuration options
 * @returns Sanitized HTML string safe for rendering
 */
export const sanitizeHTML = (
  dirty: string,
  options: DOMPurify.Config = {}
): string => {
  if (!dirty) return '';

  const defaultConfig: DOMPurify.Config = {
    ALLOWED_TAGS: [
      'b', 'i', 'em', 'strong', 'a', 'p', 'br', 'span', 'div',
      'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'table', 'thead', 'tbody', 'tr', 'th', 'td', 'img', 'code', 'pre'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'id', 'src', 'alt', 'title'],
    ALLOW_DATA_ATTR: false,
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    ...options
  };

  try {
    return DOMPurify.sanitize(dirty, defaultConfig) as string;
  } catch (error) {
    logger.error('SECURITY', 'Error sanitizing HTML', error);
    return '';
  }
};

/**
 * Sanitize rich text content with extended allowed tags
 *
 * @param dirty - Potentially unsafe HTML string
 * @returns Sanitized HTML string safe for rendering
 */
export const sanitizeRichText = (dirty: string): string => {
  return sanitizeHTML(dirty, {
    ALLOWED_TAGS: [
      'b', 'i', 'em', 'strong', 'a', 'p', 'br', 'span', 'div',
      'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
      'blockquote', 'code', 'pre', 'hr', 'img', 'figure', 'figcaption'
    ],
    ALLOWED_ATTR: [
      'href', 'target', 'rel', 'class', 'id', 'src', 'alt', 'title',
      'width', 'height', 'align', 'colspan', 'rowspan'
    ]
  });
};

/**
 * Validate and sanitize URLs to prevent open redirect attacks
 *
 * @param url - URL to validate
 * @param allowedDomains - List of allowed domains (optional)
 * @returns true if URL is safe, false otherwise
 */
export const validateURL = (
  url: string,
  allowedDomains: string[] = []
): boolean => {
  if (!url) return false;

  try {
    const urlObj = new URL(url);

    // Block javascript: and data: protocols (except safe data URIs)
    const dangerousProtocols = ['javascript:', 'vbscript:', 'file:'];
    if (dangerousProtocols.some(proto => url.toLowerCase().startsWith(proto))) {
      logger.warn('SECURITY', 'Blocked dangerous protocol in URL', { url });
      return false;
    }

    // If no allowed domains specified, allow all HTTPS/HTTP URLs
    if (allowedDomains.length === 0) {
      return urlObj.protocol === 'https:' || urlObj.protocol === 'http:';
    }

    // Check if domain is in allowed list
    const isAllowed = allowedDomains.some(domain =>
      urlObj.hostname === domain || urlObj.hostname.endsWith('.' + domain)
    );

    if (!isAllowed) {
      logger.warn('SECURITY', 'Blocked URL from untrusted domain', {
        url,
        domain: urlObj.hostname
      });
    }

    return isAllowed;
  } catch (error) {
    logger.error('SECURITY', 'Invalid URL format', { url });
    return false;
  }
};

/**
 * Get safe URL or return fallback
 *
 * @param url - URL to validate
 * @param fallback - Fallback URL if validation fails
 * @param allowedDomains - List of allowed domains (optional)
 * @returns Safe URL or fallback
 */
export const getSafeURL = (
  url: string,
  fallback: string = '#',
  allowedDomains: string[] = []
): string => {
  return validateURL(url, allowedDomains) ? url : fallback;
};

/**
 * Validate postMessage origin against allowed origins
 *
 * @param origin - Origin from message event
 * @param allowedOrigins - List of allowed origins
 * @returns true if origin is allowed, false otherwise
 */
export const validateOrigin = (
  origin: string,
  allowedOrigins: string[]
): boolean => {
  if (!origin || !allowedOrigins || allowedOrigins.length === 0) {
    return false;
  }

  const isAllowed = allowedOrigins.includes(origin);

  if (!isAllowed) {
    logger.warn('SECURITY', 'Blocked message from unauthorized origin', { origin });
  }

  return isAllowed;
};

/**
 * Get allowed origins for cross-origin messaging
 *
 * @returns Array of allowed origins
 */
export const getAllowedOrigins = (): string[] => {
  const origins = [
    window.location.origin,
    process.env.REACT_APP_AUTH_URL,
    process.env.REACT_APP_SERVER_URL,
    process.env.REACT_APP_MAIN_APP_URL
  ].filter((origin): origin is string => Boolean(origin));

  return [...new Set(origins)]; // Remove duplicates
};

/**
 * Safely set element text content (prevents XSS via textContent)
 *
 * @param element - DOM element
 * @param text - Text content to set
 */
export const safeSetTextContent = (
  element: HTMLElement | null,
  text: string
): void => {
  if (!element) return;
  element.textContent = text;
};

/**
 * Safely set element HTML content (sanitizes before setting)
 *
 * @param element - DOM element
 * @param html - HTML content to set (will be sanitized)
 * @param options - DOMPurify configuration options
 */
export const safeSetInnerHTML = (
  element: HTMLElement | null,
  html: string,
  options: DOMPurify.Config = {}
): void => {
  if (!element) return;
  element.innerHTML = sanitizeHTML(html, options);
};

/**
 * Safely clear element content
 *
 * @param element - DOM element to clear
 */
export const safeClearElement = (
  element: HTMLElement | null
): void => {
  if (!element) return;

  // Use replaceChildren for modern browsers
  if ('replaceChildren' in element) {
    element.replaceChildren();
  } else {
    // Fallback for older browsers
    const el = element as HTMLElement;
    while (el.firstChild) {
      el.removeChild(el.firstChild);
    }
  }
};

/**
 * SQL Injection detection patterns - comprehensive list
 */
const SQL_INJECTION_PATTERNS = [
  // Direct SQL injection attempts
  /(\bOR\b|\bAND\b)\s*[\d\w]+\s*=\s*[\d\w]+/gi,
  /'?\s*(OR|AND)\s+'?\d+'?\s*=\s*'?\d+'?/gi,
  /'?\s*UNION\s+.*SELECT/gi,
  /;\s*(DROP|DELETE|UPDATE|INSERT|ALTER|TRUNCATE|CREATE|EXEC|EXECUTE)/gi,
  /\/\*.*\*\/|\-\-|\#/g, // SQL comments
  
  // Stacked queries
  /;.*(DROP|DELETE|UPDATE|INSERT|ALTER|TRUNCATE|CREATE|EXEC|EXECUTE)/gi,
  
  // Boolean-based blind injection
  /'?\s*(OR|AND)\s+'?\d+'?\s*(=|<|>|!=|<>)\s*'?\d+'?/gi,
  
  // Time-based injection
  /(WAITFOR|SLEEP|DELAY|BENCHMARK|PG_SLEEP)\s*\(/gi,
  
  // Information schema attacks
  /INFORMATION_SCHEMA/gi,
  /sys\.(tables|columns|databases|users)/gi,
  
  // Function-based injection
  /(CONCAT|SUBSTRING|ASCII|CHAR|BENCHMARK|LOAD_FILE|INTO\s+OUTFILE|INTO\s+DUMPFILE)/gi,
  
  // Charset/encoding attacks
  /(CHARSET|CHARACTER_SET|COLLATE)\s*=.*[';]/gi,
  
  // Hex/Unicode encoding
  /0x[0-9a-f]+|\\x[0-9a-f]+|%[0-9a-f]{2}/gi,
];

/**
 * Dangerous SQL operations patterns
 */
const DANGEROUS_OPERATIONS = [
  /DROP\s+(TABLE|DATABASE|SCHEMA|INDEX|VIEW|PROCEDURE|FUNCTION|TRIGGER)/gi,
  /TRUNCATE\s+TABLE/gi,
  /DELETE\s+FROM\s+\w+\s*(WHERE\s+1\s*=\s*1|\s*;|$)/gi, // DELETE without proper WHERE or with always-true
  /UPDATE\s+\w+\s+SET\s+.*(WHERE\s+1\s*=\s*1|\s*;|$)/gi, // UPDATE without proper WHERE or with always-true
  /ALTER\s+(TABLE|DATABASE|SCHEMA|USER|ROLE)/gi,
  /GRANT\s+/gi,
  /REVOKE\s+/gi,
  /CREATE\s+(TABLE|DATABASE|SCHEMA|USER|ROLE|PROCEDURE|FUNCTION|TRIGGER)/gi,
  /EXEC\s+|EXECUTE\s+|CALL\s+/gi,
  /SHUTDOWN|RESTART|KILL/gi,
  /BACKUP\s+DATABASE|RESTORE\s+DATABASE/gi,
  /BULK\s+INSERT/gi,
  /OPENROWSET|OPENQUERY|OPENDATASOURCE/gi,
  /xp_cmdshell|xp_regread|sp_executesql/gi, // SQL Server specific
  /LOAD\s+DATA|LOAD_FILE|INTO\s+(OUTFILE|DUMPFILE)/gi, // MySQL specific
  /COPY\s+.*FROM|\\copy/gi, // PostgreSQL specific
];

/**
 * Comprehensive injection detection wrapper
 * Detects all types of injection attacks
 * 
 * @param input - Input string to validate
 * @param context - Context where input will be used
 * @returns Comprehensive detection result
 */
export const validateInputSecurity = (
  input: string,
  context: 'query' | 'url' | 'header' | 'body' | 'filename' | 'general' | 'curl' = 'general'
): InjectionDetectionResult => {
  return detectInjections(input, context);
};

/**
 * Validate SQL query for dangerous operations and injection attempts
 * Note: This is NOT a substitute for server-side validation and parameterized queries
 * Uses comprehensive injection detection for all attack types
 *
 * @param query - SQL query string
 * @returns Object with isValid flag, isDangerous flag, and detected threats array
 */
export const validateSQLQuery = (query: string): {
  isValid: boolean;
  isDangerous: boolean;
  threats: string[];
  severity: 'safe' | 'warning' | 'dangerous' | 'critical';
} => {
  // Use comprehensive injection detection
  const result = detectInjections(query, 'query');
  
  // Map to legacy return format for backward compatibility
  return {
    isValid: result.isSafe,
    isDangerous: result.severity === 'high' || result.severity === 'critical',
    threats: result.threats.map(t => `${t.type}: ${t.description}`),
    severity: result.severity === 'safe' ? 'safe' : 
              result.severity === 'low' ? 'warning' :
              result.severity === 'medium' ? 'warning' :
              result.severity === 'high' ? 'dangerous' : 'critical'
  };
};

/**
 * Sanitize SQL query input to remove potentially dangerous characters
 * Note: This should be used for display/logging only, not for actual query execution
 * Always use parameterized queries/prepared statements for execution
 *
 * @param query - SQL query string
 * @returns Sanitized query string safe for display
 */
export const sanitizeSQLQuery = (query: string): string => {
  if (!query) return '';

  // Remove null bytes and control characters (except newlines and tabs)
  let sanitized = query.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '');

  // Remove SQL comments (but preserve for display in some contexts)
  // Uncomment if needed: sanitized = sanitized.replace(/\/\*.*?\*\//g, '').replace(/--.*$/gm, '');

  // Escape HTML entities to prevent XSS if displaying in HTML
  sanitized = sanitized
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  return sanitized;
};

/**
 * Escape special characters in SQL query strings (for parameterized queries)
 * Note: Use parameterized queries/prepared statements instead when possible
 *
 * @param value - Value to escape
 * @returns Escaped value
 */
export const escapeSQLString = (value: string): string => {
  if (typeof value !== 'string') return value;

  return value
    .replace(/\\/g, '\\\\')  // Escape backslashes
    .replace(/'/g, "''")      // Escape single quotes
    .replace(/\x00/g, '\\0')  // Escape null bytes
    .replace(/\n/g, '\\n')    // Escape newlines
    .replace(/\r/g, '\\r')    // Escape carriage returns
    .replace(/\x1a/g, '\\Z'); // Escape Ctrl+Z
};

/**
 * Escape HTML entities in a string to prevent XSS attacks
 *
 * @param text - Text to escape
 * @returns Escaped text safe for HTML rendering
 */
export const escapeHTML = (text: string): string => {
  if (!text) return '';

  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

/**
 * Decode HTML entities in a string back to their original characters.
 * Handles named entities (&amp;, &lt;, &gt;, &quot;, &#39;) and
 * numeric/hex entities (&#x2F;, &#47;, etc.)
 *
 * @param text - Text potentially containing HTML entities
 * @returns Decoded text with entities replaced by actual characters
 */
export const decodeHTMLEntities = (text: string): string => {
  if (!text || typeof text !== 'string') return text;
  // Quick check: if no ampersand, nothing to decode
  if (!text.includes('&')) return text;

  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

/**
 * Generate CSRF token (if backend supports it)
 * This should be called after login to get CSRF token from backend
 * 
 * @returns CSRF token string or null if not supported
 */
export const getCSRFToken = (): string | null => {
  // Check if token exists in meta tag (common pattern)
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  if (metaTag) {
    return metaTag.getAttribute('content');
  }
  
  // Check localStorage/cookie (implementation-dependent)
  const tokenFromStorage = localStorage.getItem('csrf-token');
  if (tokenFromStorage) {
    return tokenFromStorage;
  }
  
  // Check cookie
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'XSRF-TOKEN' || name === 'csrf-token' || name === '_csrf') {
      return decodeURIComponent(value);
    }
  }
  
  return null;
};

/**
 * Validate CSRF token format
 * 
 * @param token - CSRF token to validate
 * @returns true if token format is valid
 */
export const validateCSRFToken = (token: string | null): boolean => {
  if (!token) return false;
  
  // Basic format validation (adjust based on your token format)
  // Common formats: UUID, base64, random string (min 32 chars)
  if (token.length < 32) return false;
  
  // Should not contain dangerous characters
  if (/[<>'"]/.test(token)) return false;
  
  return true;
};

/**
 * Create a safe DOM element with text content
 *
 * @param tagName - HTML tag name
 * @param textContent - Text content
 * @param className - Optional CSS class
 * @returns Created DOM element
 */
export const createSafeElement = (
  tagName: string,
  textContent: string = '',
  className: string = ''
): HTMLElement => {
  const element = document.createElement(tagName);
  if (textContent) {
    element.textContent = textContent;
  }
  if (className) {
    element.className = className;
  }
  return element;
};

/**
 * Configuration for allowed message methods in cross-origin communication
 */
export const ALLOWED_MESSAGE_METHODS = ['get', 'set', 'remove'] as const;

export type AllowedMessageMethod = typeof ALLOWED_MESSAGE_METHODS[number];

/**
 * Validate message method for cross-origin communication
 *
 * @param method - Method name to validate
 * @returns true if method is allowed, false otherwise
 */
export const validateMessageMethod = (method: string): boolean => {
  return ALLOWED_MESSAGE_METHODS.includes(method as AllowedMessageMethod);
};

/**
 * File Upload Security
 */

// Allowed file extensions for different upload types
export const ALLOWED_FILE_TYPES = {
  json: ['.json'],
  csv: ['.csv'],
  postman: ['.json', '.postman_collection'],
  script: ['.js', '.py', '.sh', '.bat'],
  all: ['.json', '.csv', '.txt', '.xml']
} as const;

// Maximum file sizes (in bytes)
export const MAX_FILE_SIZES = {
  json: 10 * 1024 * 1024, // 10MB
  csv: 50 * 1024 * 1024,  // 50MB
  default: 5 * 1024 * 1024 // 5MB
} as const;

/**
 * Validate file extension
 *
 * @param fileName - Name of the file
 * @param allowedExtensions - Array of allowed extensions
 * @returns true if file extension is allowed
 */
export const validateFileExtension = (
  fileName: string,
  allowedExtensions: readonly string[]
): boolean => {
  if (!fileName) return false;

  const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
  return allowedExtensions.includes(extension);
};

/**
 * Validate file size
 *
 * @param fileSize - Size of file in bytes
 * @param maxSize - Maximum allowed size in bytes
 * @returns true if file size is within limit
 */
export const validateFileSize = (
  fileSize: number,
  maxSize: number = MAX_FILE_SIZES.default
): boolean => {
  return fileSize > 0 && fileSize <= maxSize;
};

/**
 * Validate file for upload
 *
 * @param file - File object to validate
 * @param allowedExtensions - Array of allowed extensions
 * @param maxSize - Maximum file size in bytes
 * @returns Object with validation result and error message
 */
export const validateFileUpload = (
  file: File,
  allowedExtensions: readonly string[],
  maxSize: number = MAX_FILE_SIZES.default
): { valid: boolean; error?: string } => {
  if (!file) {
    return { valid: false, error: 'No file selected' };
  }

  // Validate file extension
  if (!validateFileExtension(file.name, allowedExtensions)) {
    return {
      valid: false,
      error: `Invalid file type. Allowed: ${allowedExtensions.join(', ')}`
    };
  }

  // Validate file size
  if (!validateFileSize(file.size, maxSize)) {
    const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(2);
    return {
      valid: false,
      error: `File too large. Maximum size: ${maxSizeMB}MB`
    };
  }

  return { valid: true };
};

/**
 * Sanitize filename to prevent path traversal
 *
 * @param fileName - Original filename
 * @returns Sanitized filename safe for storage
 */
export const sanitizeFileName = (fileName: string): string => {
  if (!fileName) return '';

  // Remove path separators and null bytes
  let safe = fileName.replace(/[\/\\:\0]/g, '_');

  // Remove leading dots to prevent hidden files
  safe = safe.replace(/^\.+/, '');

  // Limit filename length
  const maxLength = 255;
  if (safe.length > maxLength) {
    const extension = safe.substring(safe.lastIndexOf('.'));
    const nameWithoutExt = safe.substring(0, safe.lastIndexOf('.'));
    safe = nameWithoutExt.substring(0, maxLength - extension.length) + extension;
  }

  return safe || 'unnamed_file';
};

/**
 * Validate JSON file content
 *
 * @param content - File content as string
 * @returns Object with validation result and parsed JSON (if valid)
 */
export const validateJSONContent = (
  content: string
): { valid: boolean; data?: any; error?: string } => {
  try {
    const data = JSON.parse(content);
    return { valid: true, data };
  } catch (error) {
    return {
      valid: false,
      error: 'Invalid JSON format'
    };
  }
};
